import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Gift, Tag, Loader2 } from "lucide-react";
import Layout from "../components/Layout";
import { api } from "../lib/api";

const AMOUNTS = [25, 50, 100, 150];

export default function Deals() {
  const [discounts, setDiscounts] = useState([]);
  const [amount, setAmount] = useState(50);
  const [custom, setCustom] = useState("");
  const [form, setForm] = useState({ purchaser_name: "", purchaser_email: "", recipient_email: "", message: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get("/discounts").then((r) => setDiscounts(r.data.discounts)); }, []);

  const finalAmount = custom ? Math.round(parseFloat(custom) || 0) : amount;

  const buy = async () => {
    if (!form.purchaser_email) { toast.error("Enter your email so we can send the gift code."); return; }
    if (finalAmount < 10) { toast.error("Minimum gift card is $10."); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/giftcards/checkout", { ...form, amount: finalAmount, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start checkout.");
      setLoading(false);
    }
  };

  return (
    <Layout>
      <section className="max-w-6xl mx-auto px-6 py-16 md:py-24">
        <p className="text-xs tracking-[0.3em] uppercase text-pink-500 font-bold mb-4">Deals &amp; Gift Cards</p>
        <h1 className="text-4xl md:text-6xl font-extrabold tracking-tighter">Save more &amp; gift a shine</h1>
        <p className="text-zinc-400 mt-4 max-w-2xl text-lg">Our newest active coupons plus Pink Crew gift cards — the perfect present for any car owner on the windward side.</p>

        <div className="mt-12">
          <h2 className="text-2xl md:text-3xl font-bold tracking-tight mb-6 flex items-center gap-2"><Tag className="text-pink-500" size={22} /> Current deals</h2>
          {discounts.length === 0 ? (
            <p className="text-zinc-500" data-testid="deals-empty">No active deals right now — check back soon!</p>
          ) : (
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {discounts.map((d) => (
                <div key={d.id} className="rounded-2xl border border-white/10 bg-[#121212] p-6" data-testid={`deal-${d.id}`}>
                  <div className="flex items-center justify-between gap-3">
                    <h3 className="text-lg font-bold">{d.name}</h3>
                    <span className="text-pink-500 font-extrabold whitespace-nowrap">{d.percent}% off</span>
                  </div>
                  {d.description && <p className="text-sm text-zinc-400 mt-2 leading-relaxed">{d.description}</p>}
                  {d.code && <p className="mt-4 inline-block text-sm font-mono bg-white/10 rounded px-3 py-1">Code: <span className="text-pink-400 font-bold">{d.code}</span></p>}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="mt-16 grid gap-8 lg:grid-cols-2 items-start">
          <div className="rounded-3xl border border-pink-500/40 bg-gradient-to-br from-[#1a0710] to-[#121212] p-8">
            <Gift className="text-pink-500" size={40} />
            <h2 className="text-3xl font-extrabold tracking-tight mt-4">Pink Crew Gift Card</h2>
            <p className="text-zinc-300 mt-3 leading-relaxed">Give the gift of a spotless ride. Your recipient gets a unique code they can redeem at checkout on any booking — for the full value, or across multiple details.</p>
            <ul className="mt-5 space-y-2 text-sm text-zinc-400">
              <li>• Delivered instantly by email</li>
              <li>• Redeemable on any package or add-on</li>
              <li>• Never expires</li>
            </ul>
          </div>

          <div className="rounded-3xl border border-white/10 bg-[#121212] p-8">
            <p className="text-sm font-semibold mb-3">Choose an amount</p>
            <div className="grid grid-cols-4 gap-2">
              {AMOUNTS.map((a) => (
                <button key={a} onClick={() => { setAmount(a); setCustom(""); }} className={`rounded-xl border py-3 font-bold transition-all ${!custom && amount === a ? "border-pink-500 bg-[#1a1013]" : "border-white/15 hover:border-white/30"}`} data-testid={`gift-amt-${a}`}>${a}</button>
              ))}
            </div>
            <input value={custom} onChange={(e) => setCustom(e.target.value.replace(/[^0-9]/g, ""))} placeholder="Custom amount ($)" inputMode="numeric" className="mt-3 w-full bg-[#050505] border border-white/20 focus:border-pink-500 rounded-lg px-4 py-3 outline-none" data-testid="gift-amt-custom" />
            <div className="mt-4 grid gap-3">
              <input value={form.purchaser_name} onChange={(e) => setForm({ ...form, purchaser_name: e.target.value })} placeholder="Your name" className="bg-[#050505] border border-white/20 focus:border-pink-500 rounded-lg px-4 py-3 outline-none" data-testid="gift-name" />
              <input value={form.purchaser_email} onChange={(e) => setForm({ ...form, purchaser_email: e.target.value })} placeholder="Your email *" type="email" autoCapitalize="none" className="bg-[#050505] border border-white/20 focus:border-pink-500 rounded-lg px-4 py-3 outline-none" data-testid="gift-email" />
              <input value={form.recipient_email} onChange={(e) => setForm({ ...form, recipient_email: e.target.value })} placeholder="Recipient email (optional)" type="email" autoCapitalize="none" className="bg-[#050505] border border-white/20 focus:border-pink-500 rounded-lg px-4 py-3 outline-none" data-testid="gift-recipient" />
              <textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Gift message (optional)" rows={2} className="bg-[#050505] border border-white/20 focus:border-pink-500 rounded-lg px-4 py-3 outline-none" data-testid="gift-message" />
            </div>
            <button onClick={buy} disabled={loading} className="mt-5 w-full bg-pink-600 hover:bg-pink-500 rounded-full py-3.5 font-bold transition-colors active:scale-95 disabled:opacity-50 flex items-center justify-center gap-2" data-testid="gift-buy">
              {loading ? <><Loader2 className="animate-spin" size={18} /> Redirecting…</> : `Buy $${finalAmount} gift card`}
            </button>
            <p className="text-xs text-zinc-500 mt-3 text-center">Secure checkout via Stripe. The gift code is emailed the moment payment succeeds.</p>
          </div>
        </div>
      </section>
    </Layout>
  );
}
